import { useState, useEffect, useCallback, useRef } from 'react';
import { authFetch } from '../lib/authFetch';
import { useFetchUsage } from '../hooks/useFetchUsage';
import DateRangePicker, { getPresetDates, getComparisonDates, fmtDate } from '../components/ui/DateRangePicker';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { Card, CardHeader, CardTitle, CardContent, Button, Badge, Alert, AlertDescription, LastUpdated } from '../components/ui';
import { formatCurrency, formatNumber } from '../lib/utils';
import { 
  DollarSign, TrendingUp, TrendingDown, BarChart3, 
  Settings, ChevronDown, ChevronUp, Check, Calendar, ArrowRight, X,
  RefreshCw, Loader2, Zap, Info, Edit2, Save, ArrowRightLeft, Table, ShieldCheck
} from 'lucide-react';
import toast from 'react-hot-toast';
import { SkeletonPublisherPlatform } from '../components/ui/Skeleton';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

// DATE_PRESETS moved to shared DateRangePicker

// Currency list (20 major currencies)
const CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar', flag: '🇺🇸' },
  { code: 'GBP', symbol: '£', name: 'British Pound', flag: '🇬🇧' },
  { code: 'EUR', symbol: '€', name: 'Euro', flag: '🇪🇺' },
  { code: 'NGN', symbol: '₦', name: 'Nigerian Naira', flag: '🇳🇬' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee', flag: '🇮🇳' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar', flag: '🇨🇦' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar', flag: '🇦🇺' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen', flag: '🇯🇵' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan', flag: '🇨🇳' },
  { code: 'CHF', symbol: 'Fr', name: 'Swiss Franc', flag: '🇨🇭' },
  { code: 'ZAR', symbol: 'R', name: 'South African Rand', flag: '🇿🇦' },
  { code: 'KES', symbol: 'KSh', name: 'Kenyan Shilling', flag: '🇰🇪' },
  { code: 'GHS', symbol: 'GH₵', name: 'Ghanaian Cedi', flag: '🇬🇭' },
  { code: 'BRL', symbol: 'R$', name: 'Brazilian Real', flag: '🇧🇷' },
  { code: 'MXN', symbol: 'MX$', name: 'Mexican Peso', flag: '🇲🇽' },
  { code: 'PHP', symbol: '₱', name: 'Philippine Peso', flag: '🇵🇭' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar', flag: '🇸🇬' },
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham', flag: '🇦🇪' },
  { code: 'SAR', symbol: '﷼', name: 'Saudi Riyal', flag: '🇸🇦' },
  { code: 'PKR', symbol: '₨', name: 'Pakistani Rupee', flag: '🇵🇰' },
];

// Default exchange rates (1 USD = X)
const DEFAULT_EXCHANGE_RATES = {
  USD: 1,
  GBP: 0.79,
  EUR: 0.92,
  NGN: 1550,
  INR: 83.5,
  CAD: 1.36,
  AUD: 1.53,
  JPY: 149.5,
  CNY: 7.24,
  CHF: 0.88,
  ZAR: 18.5,
  KES: 153,
  GHS: 15.2,
  BRL: 4.97,
  MXN: 17.2,
  PHP: 56.5,
  SGD: 1.34,
  AED: 3.67,
  SAR: 3.75,
  PKR: 278,
};

// Helper to format date for input
const formatDateInput = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// Helper to get date range from preset
const getDateRangeFromPreset = (preset) => {
  const end = new Date();
  const start = new Date();
  
  if (preset === 'today') {
    // Just today
  } else if (preset === 'yesterday') {
    start.setDate(start.getDate() - 1);
    end.setDate(end.getDate() - 1);
  } else if (preset === '7d') {
    start.setDate(start.getDate() - 6);
  } else if (preset === '30d') {
    start.setDate(start.getDate() - 29);
  } else if (preset === '90d') {
    start.setDate(start.getDate() - 89);
  }
  
  return { start: formatDateInput(start), end: formatDateInput(end) };
};

// Helper to format relative time (e.g., "5 mins ago")
const formatRelativeTime = (dateStr) => {
  if (!dateStr) return null;
  
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  return date.toLocaleDateString();
};

export default function Publisher() {
  const { user, profile, isPro, isTrialActive, isTrialExpired, daysRemaining, tier, hasAccess } = useAuth();
  const [loading, setLoading] = useState(true);
  const [showSourceSelector, setShowSourceSelector] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const hasInitiallyLoaded = useRef(false); // Prevents showing spinner on tab switch
  
  // Fetch All state
  const [fetchingAll, setFetchingAll] = useState(false);
  const [fetchProgress, setFetchProgress] = useState(null); // { current: 'meta_ads', index: 1, total: 4 }
  // Fetch usage is read via the shared hook so Overview / Publisher /
  // Ecommerce reflect the same counter and stay in sync on tab focus.
  const { used: fetchUsedShared, limit: fetchLimitShared, remaining: fetchRemainingShared,
          lastFetchAt: fetchLastShared, refresh: refreshFetchUsage } = useFetchUsage();
  const fetchUsage = { used: fetchUsedShared, limit: fetchLimitShared, remaining: fetchRemainingShared };
  const [lastFetchTime, setLastFetchTime] = useState(null);
  
  // Connected accounts
  const [connectedSources, setConnectedSources] = useState([]);
  const [selectedSources, setSelectedSources] = useState([]);
  
  // Revenue data by source
  const [revenueData, setRevenueData] = useState({});
  const [chartData, setChartData] = useState([]);
  
  // Date range state - default to today with comparison
  const [datePreset, setDatePreset] = useState('today');
  const [startDate, setStartDate] = useState(formatDateInput(new Date()));
  const [endDate, setEndDate] = useState(formatDateInput(new Date()));
  
  // Pending date state (for date picker - only applied when clicking Apply)
  const [pendingPreset, setPendingPreset] = useState('today');
  const [pendingStartDate, setPendingStartDate] = useState(formatDateInput(new Date()));
  const [pendingEndDate, setPendingEndDate] = useState(formatDateInput(new Date()));
  
  // Comparison state - enabled by default
  const [compareEnabled, setCompareEnabled] = useState(true);
  const [comparePreset, setComparePreset] = useState('previous');
  const [compareStartDate, setCompareStartDate] = useState('');
  const [compareEndDate, setCompareEndDate] = useState('');
  const [compareData, setCompareData] = useState({});

  // Pending comparison state (for date picker - only applied when clicking Apply)
  const [pendingCompareEnabled, setPendingCompareEnabled] = useState(true);
  const [pendingComparePreset, setPendingComparePreset] = useState('previous');

  // Monthly data table state
  const [monthlyData, setMonthlyData] = useState({}); // { '2024-01': { days: [...], totals: {...} } }
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [availableMonths, setAvailableMonths] = useState([]);

  // Currency converter state
  const [exchangeRates, setExchangeRates] = useState(DEFAULT_EXCHANGE_RATES);
  const [convertAmount, setConvertAmount] = useState('1000');
  const [convertFrom, setConvertFrom] = useState('USD');
  const [convertTo, setConvertTo] = useState('NGN');
  const [showRateEditor, setShowRateEditor] = useState(false);
  const [editingRates, setEditingRates] = useState({});
  const [displayCurrency, setDisplayCurrency] = useState('USD'); // Currency for dashboard display

  // Handle preset change (updates pending state only)
  const handlePresetChange = (preset) => {
    setPendingPreset(preset);
    if (preset !== 'custom') {
      const { start, end } = getDateRangeFromPreset(preset);
      setPendingStartDate(start);
      setPendingEndDate(end);
    }
  };

  // Apply date changes
  const applyDateChanges = () => {
    setDatePreset(pendingPreset);
    setStartDate(pendingStartDate);
    setEndDate(pendingEndDate);
    setCompareEnabled(pendingCompareEnabled);
    setComparePreset(pendingComparePreset);
    setShowDatePicker(false);
  };

  // Open date picker (sync pending with current)
  const openDatePicker = () => {
    setPendingPreset(datePreset);
    setPendingStartDate(startDate);
    setPendingEndDate(endDate);
    setPendingCompareEnabled(compareEnabled);
    setPendingComparePreset(comparePreset);
    setShowDatePicker(true);
  };

  // Get comparison date range (previous period) - uses pending state for preview
  const getComparisonRangePreview = useCallback(() => {
    const start = new Date(pendingStartDate);
    const end = new Date(pendingEndDate);
    const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
    
    if (pendingComparePreset === 'previous') {
      const compEnd = new Date(start);
      compEnd.setDate(compEnd.getDate() - 1);
      const compStart = new Date(compEnd);
      compStart.setDate(compStart.getDate() - daysDiff + 1);
      return { start: formatDateInput(compStart), end: formatDateInput(compEnd) };
    } else if (pendingComparePreset === 'year') {
      const compStart = new Date(start);
      compStart.setFullYear(compStart.getFullYear() - 1);
      const compEnd = new Date(end);
      compEnd.setFullYear(compEnd.getFullYear() - 1);
      return { start: formatDateInput(compStart), end: formatDateInput(compEnd) };
    }
    return { start: '', end: '' };
  }, [pendingStartDate, pendingEndDate, pendingComparePreset]);

  // Get comparison date range (previous period)
  const getComparisonRange = useCallback(() => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
    
    if (comparePreset === 'previous') {
      const compEnd = new Date(start);
      compEnd.setDate(compEnd.getDate() - 1);
      const compStart = new Date(compEnd);
      compStart.setDate(compStart.getDate() - daysDiff + 1);
      return { start: formatDateInput(compStart), end: formatDateInput(compEnd) };
    } else if (comparePreset === 'year') {
      const compStart = new Date(start);
      compStart.setFullYear(compStart.getFullYear() - 1);
      const compEnd = new Date(end);
      compEnd.setFullYear(compEnd.getFullYear() - 1);
      return { start: formatDateInput(compStart), end: formatDateInput(compEnd) };
    }
    return { start: compareStartDate, end: compareEndDate };
  }, [startDate, endDate, comparePreset, compareStartDate, compareEndDate]);

  // Fetch usage is sourced from useFetchUsage — keep `loadFetchUsage` as an
  // alias so callsites that trigger a refresh after a fetch keep working.
  const loadFetchUsage = refreshFetchUsage;
  useEffect(() => {
    if (fetchLastShared) setLastFetchTime(new Date(fetchLastShared));
  }, [fetchLastShared]);

  // Load exchange rates from Supabase
  const loadExchangeRates = useCallback(async () => {
    if (!user) return;
    
    try {
      const { data: settings, error } = await supabase
        .from('user_settings')
        .select('exchange_rates')
        .eq('user_id', user.id)
        .maybeSingle();
      
      // If table doesn't exist or no data, use defaults
      if (error) {
        console.log('user_settings not found, using defaults');
        return;
      }
      
      if (settings?.exchange_rates) {
        setExchangeRates({ ...DEFAULT_EXCHANGE_RATES, ...settings.exchange_rates });
      }
    } catch (e) {
      console.log('Error loading exchange rates:', e);
    }
  }, [user]);

  // Save exchange rates to Supabase
  const saveExchangeRates = async (rates) => {
    if (!user) return;
    
    try {
      // First try to check if record exists
      const { data: existing } = await supabase
        .from('user_settings')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();
      
      let error;
      
      if (existing) {
        // Update existing record
        const result = await supabase
          .from('user_settings')
          .update({
            exchange_rates: rates,
            updated_at: new Date().toISOString()
          })
          .eq('user_id', user.id);
        error = result.error;
      } else {
        // Insert new record
        const result = await supabase
          .from('user_settings')
          .insert({
            user_id: user.id,
            exchange_rates: rates,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });
        error = result.error;
      }
      
      if (error) {
        console.error('Save error:', error);
        toast.error(`Failed to save: ${error.message}`);
        return;
      }
      
      setExchangeRates(rates);
      setShowRateEditor(false);
      toast.success('Exchange rates saved');
    } catch (e) {
      console.error('Save exception:', e);
      toast.error('Failed to save exchange rates');
    }
  };

  // Convert currency
  const convertCurrency = (amount, from, to) => {
    const amountInUSD = from === 'USD' ? amount : amount / exchangeRates[from];
    return to === 'USD' ? amountInUSD : amountInUSD * exchangeRates[to];
  };

  // Format currency with display currency conversion
  const formatDisplayCurrency = (amountUSD) => {
    if (displayCurrency === 'USD') {
      return formatCurrency(amountUSD);
    }
    const converted = amountUSD * exchangeRates[displayCurrency];
    const symbol = CURRENCIES.find(c => c.code === displayCurrency)?.symbol || '';
    return `${symbol}${converted.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Load monthly data for last 3 months
  const loadMonthlyData = useCallback(async () => {
    if (!user || selectedSources.length === 0) return;
    
    const months = [];
    const now = new Date();
    
    // Get last 3 months
    for (let i = 0; i < 3; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const year  = d.getFullYear();
      const month = d.getMonth() + 1;
      const monthKey  = `${year}-${String(month).padStart(2, '0')}`;
      const lastDay   = new Date(year, month, 0).getDate();
      const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
      const endDate   = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      months.push({ key: monthKey, start: startDate, end: endDate, label: d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) });
    }
    
    setAvailableMonths(months);
    
    const data = {};
    
    for (const month of months) {
      const days = {};
      const daysInMonth = new Date(parseInt(month.key.split('-')[0]), parseInt(month.key.split('-')[1]), 0).getDate();
      
      // Initialize all days
      for (let d = 1; d <= daysInMonth; d++) {
        const dateStr = `${month.key}-${String(d).padStart(2, '0')}`;
        days[dateStr] = { date: dateStr, revenue: 0, spend: 0, profit: 0, roas: 0 };
      }
      
      // Aggregate data from all selected sources
      for (const sourceId of selectedSources) {
        const source = connectedSources.find(s => s.id === sourceId);
        if (!source) continue;
        
        let dailyData = [];
        
        // Fetch data based on source type
        if (source.type === 'admanager') {
          const { data: adm } = await supabase
            .from('admanager_daily')
            .select('date, net_revenue, impressions, clicks')
            .eq('user_id', user.id)
            .gte('date', month.start)
            .lte('date', month.end);
          dailyData = (adm || []).map(d => ({ date: d.date, revenue: d.net_revenue || 0, spend: 0 }));
        } else if (source.type === 'adsense') {
          const { data: ads } = await supabase
            .from('adsense_daily')
            .select('date, net_revenue')
            .eq('user_id', user.id)
            .gte('date', month.start)
            .lte('date', month.end);
          dailyData = (ads || []).map(d => ({ date: d.date, revenue: d.net_revenue || 0, spend: 0 }));
        } else if (source.type === 'meta') {
          const accountId = sourceId.replace('meta_', '');
          const { data: meta } = await supabase
            .from('meta_daily')
            .select('date, spend')
            .eq('meta_account_id', accountId)
            .gte('date', month.start)
            .lte('date', month.end);
          dailyData = (meta || []).map(d => ({ date: d.date, revenue: 0, spend: d.spend || 0 }));
        } else if (source.type === 'googleads') {
          const accountId = sourceId.replace('googleads_', '');
          const { data: gads } = await supabase
            .from('google_ads_daily')
            .select('date, spend')
            .eq('account_id', accountId)
            .gte('date', month.start)
            .lte('date', month.end);
          dailyData = (gads || []).map(d => ({ date: d.date, revenue: 0, spend: d.spend || 0 }));
        }
        
        // Aggregate into days
        for (const d of dailyData) {
          if (days[d.date]) {
            days[d.date].revenue += d.revenue || 0;
            days[d.date].spend += d.spend || 0;
          }
        }
      }
      
      // Calculate profit and ROAS for each day
      let monthTotals = { revenue: 0, spend: 0, profit: 0, roas: 0 };
      
      Object.values(days).forEach(day => {
        day.profit = day.revenue - day.spend;
        day.roas = day.spend > 0 ? day.revenue / day.spend : 0;
        monthTotals.revenue += day.revenue;
        monthTotals.spend += day.spend;
      });
      
      monthTotals.profit = monthTotals.revenue - monthTotals.spend;
      monthTotals.roas = monthTotals.spend > 0 ? monthTotals.revenue / monthTotals.spend : 0;
      
      data[month.key] = { days, totals: monthTotals, label: month.label };
    }
    
    setMonthlyData(data);
  }, [user, selectedSources, connectedSources]);

  // Fetch all platform data
  const handleFetchAll = async () => {
    if (fetchUsage.remaining <= 0) {
      toast.error(`Daily fetch limit reached (${fetchUsage.limit}/${fetchUsage.limit}). ${!isPro ? 'Upgrade to Pro for 10 fetches/day.' : ''}`);
      return;
    }

    setFetchingAll(true);
    setFetchProgress({ current: 'Starting...', index: 0, total: 4 });
    const tid = toast.loading('Fetching data from all platforms...');

    try {
      // Show progress for each platform
      const platforms = ['meta_ads', 'admanager', 'adsense', 'google_ads'];
      const platformNames = {
        meta_ads: 'Meta Ads',
        admanager: 'Ad Manager', 
        adsense: 'AdSense',
        google_ads: 'Google Ads'
      };
      
      // Update progress as we go (simulated - actual progress comes from API)
      let progressIndex = 0;
      const progressInterval = setInterval(() => {
        if (progressIndex < platforms.length) {
          setFetchProgress({
            current: platformNames[platforms[progressIndex]],
            index: progressIndex + 1,
            total: platforms.length
          });
          progressIndex++;
        }
      }, 2000);

      const res = await authFetch('/api/fetch-all-data', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id }),
      });

      clearInterval(progressInterval);
      setFetchProgress(null);

      const data = await res.json();

      if (res.status === 429) {
        toast.error(data.message || 'Daily fetch limit reached', { id: tid });
        return;
      }

      if (data.success) {
        const { summary, usage, results } = data;

        // Refresh the shared counter — Overview & Ecommerce pick this up too.
        refreshFetchUsage();
        setLastFetchTime(new Date());
        void usage;  // server response also includes the latest snapshot

        // Build detailed message
        const succeeded = [];
        const failed = [];
        
        if (results?.meta_ads?.success) succeeded.push('Meta Ads');
        else if (results?.meta_ads?.error && results.meta_ads.error !== 'Not configured') failed.push('Meta Ads');
        
        if (results?.admanager?.success) succeeded.push('Ad Manager');
        else if (results?.admanager?.error && results.admanager.error !== 'Not configured') failed.push('Ad Manager');
        
        if (results?.adsense?.success) succeeded.push('AdSense');
        else if (results?.adsense?.error && results.adsense.error !== 'Not configured') failed.push('AdSense');
        
        if (results?.google_ads?.success) succeeded.push('Google Ads');
        else if (results?.google_ads?.error && results.google_ads.error !== 'Not configured') failed.push('Google Ads');

        // Show success message
        if (summary.platforms_succeeded > 0) {
          const msg = `Fetched ${summary.total_records} records from ${succeeded.join(', ')}`;
          if (failed.length > 0) {
            toast.success(`${msg}. Failed: ${failed.join(', ')}`, { id: tid, duration: 5000 });
          } else {
            toast.success(msg, { id: tid });
          }
        } else if (summary.platforms_configured === 0) {
          toast.error('No platforms configured. Connect your accounts first.', { id: tid });
        } else {
          toast.error(`Failed to fetch: ${failed.join(', ')}`, { id: tid });
        }

        // Reload dashboard data
        loadData();
        loadMonthlyData();
      } else {
        toast.error(data.error || 'Failed to fetch data', { id: tid });
      }
    } catch (error) {
      console.error('Fetch all error:', error);
      toast.error('Failed to fetch data', { id: tid });
      setFetchProgress(null);
    } finally {
      setFetchingAll(false);
      setFetchProgress(null);
    }
  };

  // Load connected accounts and their data
  const loadData = useCallback(async () => {
    if (!user) return;
    if (!hasInitiallyLoaded.current) setLoading(true);

    try {
      const startStr = startDate;
      const endStr   = endDate;

      // ── Phase 1: Fetch all account settings in parallel ─────────────────
      const [
        { data: adManagerSettings },
        { data: adsenseSettings },
        { data: metaAccounts },
        { data: googleAdsAccounts },
        { data: customAdAccount },
        { data: customRevenueAccount },
      ] = await Promise.all([
        supabase.from('admanager_settings').select('*').eq('user_id', user.id).maybeSingle(),
        supabase.from('adsense_settings').select('*').eq('user_id', user.id).maybeSingle(),
        supabase.from('meta_accounts').select('*').eq('user_id', user.id).eq('show_on_dashboard', true),
        supabase.from('google_ads_accounts').select('*').eq('user_id', user.id),
        supabase.from('custom_ad_accounts').select('*').eq('user_id', user.id).eq('show_on_dashboard', true).maybeSingle(),
        supabase.from('custom_revenue_accounts').select('*').eq('user_id', user.id).eq('show_on_dashboard', true).maybeSingle(),
      ]);

      // ── Build sources list ───────────────────────────────────────────────
      const sources = [];
      if (adManagerSettings?.is_connected)      sources.push({ id: 'admanager',                     type: 'admanager',       name: 'Google Ad Manager',                                      icon: '📊', details: `Network: ${adManagerSettings.network_code}`,           lastFetch: adManagerSettings.last_fetch });
      if (adsenseSettings?.is_connected)        sources.push({ id: 'adsense',                       type: 'adsense',         name: 'Google AdSense',                                         icon: '💰', details: adsenseSettings.account_name || adsenseSettings.pub_id, lastFetch: adsenseSettings.last_fetch });
      (metaAccounts || []).forEach(a =>         sources.push({ id: `meta_${a.id}`,                  type: 'meta',            name: `Meta Ads: ${a.account_name}`,                            icon: '📱', details: a.account_id ? `ID: ${a.account_id}` : 'Manual',       lastFetch: a.last_fetch }));
      (googleAdsAccounts || []).forEach(a =>    sources.push({ id: `googleads_${a.id}`,             type: 'googleads',       name: `Google Ads: ${a.account_name || a.customer_id}`,         icon: '🔍', details: `ID: ${a.customer_id}`,                                lastFetch: a.last_fetch }));
      if (customAdAccount)                      sources.push({ id: `custom_ad_${customAdAccount.id}`,      type: 'custom_ad',  name: customAdAccount.name,      icon: '📊', details: 'Custom Ad Account',      lastFetch: customAdAccount.last_fetch });
      if (customRevenueAccount)                 sources.push({ id: `custom_revenue_${customRevenueAccount.id}`, type: 'custom_revenue', name: customRevenueAccount.name, icon: '💵', details: 'Custom Revenue Account', lastFetch: customRevenueAccount.last_fetch });

      // ── Phase 2: Fetch all daily data in parallel ────────────────────────
      const dailyFetches = [];
      if (adManagerSettings?.is_connected)   dailyFetches.push(['admanager',   supabase.from('admanager_daily').select('date,net_revenue,impressions,clicks').eq('user_id',user.id).gte('date',startStr).lte('date',endStr)]);
      if (adsenseSettings?.is_connected)     dailyFetches.push(['adsense',     supabase.from('adsense_daily').select('date,net_revenue,page_views,clicks').eq('user_id',user.id).gte('date',startStr).lte('date',endStr)]);
      (metaAccounts || []).forEach(a =>      dailyFetches.push([`meta_${a.id}`,        supabase.from('meta_daily').select('date,spend,impressions,clicks').eq('meta_account_id',a.id).gte('date',startStr).lte('date',endStr)]));
      (googleAdsAccounts || []).forEach(a => dailyFetches.push([`googleads_${a.id}`,   supabase.from('google_ads_daily').select('date,spend,impressions,clicks,conversions').eq('account_id',a.id).gte('date',startStr).lte('date',endStr)]));
      if (customAdAccount)                   dailyFetches.push([`custom_ad_${customAdAccount.id}`,      supabase.from('custom_ad_daily').select('date,spend,impressions,clicks,conversions').eq('account_id',customAdAccount.id).gte('date',startStr).lte('date',endStr)]);
      if (customRevenueAccount)              dailyFetches.push([`custom_revenue_${customRevenueAccount.id}`, supabase.from('custom_revenue_daily').select('date,revenue,impressions,clicks,page_views').eq('account_id',customRevenueAccount.id).gte('date',startStr).lte('date',endStr)]);

      const dailyResults = await Promise.all(dailyFetches.map(([, q]) => q));

      // ── Build data map ───────────────────────────────────────────────────
      const data = {};
      dailyFetches.forEach(([id], i) => {
        const rows = dailyResults[i].data || [];
        if (id === 'admanager')   data[id] = { daily: rows, totals: calculateTotals(rows, 'net_revenue', 'impressions', 'clicks') };
        else if (id === 'adsense') data[id] = { daily: rows, totals: calculateTotals(rows, 'net_revenue', 'page_views', 'clicks') };
        else if (id.startsWith('meta_'))           data[id] = { daily: rows, totals: calculateMetaTotals(rows),          isSpend: true };
        else if (id.startsWith('googleads_'))      data[id] = { daily: rows, totals: calculateGoogleAdsTotals(rows),     isSpend: true };
        else if (id.startsWith('custom_ad_'))      data[id] = { daily: rows, totals: calculateCustomAdTotals(rows),      isSpend: true };
        else if (id.startsWith('custom_revenue_')) data[id] = { daily: rows, totals: calculateCustomRevenueTotals(rows), isSpend: false };
      });

      setConnectedSources(sources);
      setRevenueData(data);

      const savedSelection = localStorage.getItem(`dashboard_sources_${user.id}`);
      if (savedSelection) {
        const parsed = JSON.parse(savedSelection);
        const valid  = parsed.filter(id => sources.some(s => s.id === id));
        setSelectedSources(valid.length > 0 ? valid : sources.map(s => s.id));
      } else {
        setSelectedSources(sources.map(s => s.id));
      }

      if (compareEnabled) await loadComparisonData(sources, data);

    } catch (error) {
      console.error('Dashboard load error:', error);
      if (!hasInitiallyLoaded.current) toast.error('Failed to load dashboard data');
    }

    hasInitiallyLoaded.current = true;
    setLoading(false);
  }, [user, startDate, endDate, compareEnabled]);

  // Load comparison data
  const loadComparisonData = async (sources, currentData) => {
    const { start: compStart, end: compEnd } = getComparisonRange();
    const compData = {};

    for (const source of sources) {
      if (source.type === 'admanager') {
        const { data: daily } = await supabase
          .from('admanager_daily')
          .select('date, net_revenue, impressions, clicks')
          .eq('user_id', user.id)
          .gte('date', compStart)
          .lte('date', compEnd);
        compData[source.id] = {
          daily: daily || [],
          totals: calculateTotals(daily || [], 'net_revenue', 'impressions', 'clicks')
        };
      } else if (source.type === 'adsense') {
        const { data: daily } = await supabase
          .from('adsense_daily')
          .select('date, net_revenue, page_views, clicks')
          .eq('user_id', user.id)
          .gte('date', compStart)
          .lte('date', compEnd);
        compData[source.id] = {
          daily: daily || [],
          totals: calculateTotals(daily || [], 'net_revenue', 'page_views', 'clicks')
        };
      } else if (source.type === 'meta') {
        const accountId = source.id.replace('meta_', '');
        const { data: daily } = await supabase
          .from('meta_daily')
          .select('date, spend, impressions, clicks')
          .eq('meta_account_id', accountId)
          .gte('date', compStart)
          .lte('date', compEnd);
        compData[source.id] = {
          daily: daily || [],
          totals: calculateMetaTotals(daily || []),
          isSpend: true
        };
      } else if (source.type === 'googleads') {
        const accountId = source.id.replace('googleads_', '');
        const { data: daily } = await supabase
          .from('google_ads_daily')
          .select('date, spend, impressions, clicks')
          .eq('account_id', accountId)
          .gte('date', compStart)
          .lte('date', compEnd);
        compData[source.id] = {
          daily: daily || [],
          totals: calculateGoogleAdsTotals(daily || []),
          isSpend: true
        };
      } else if (source.type === 'custom_ad') {
        const accountId = source.id.replace('custom_ad_', '');
        const { data: daily } = await supabase
          .from('custom_ad_daily')
          .select('date, spend, impressions, clicks, conversions')
          .eq('account_id', accountId)
          .gte('date', compStart)
          .lte('date', compEnd);
        compData[source.id] = {
          daily: daily || [],
          totals: calculateCustomAdTotals(daily || []),
          isSpend: true
        };
      } else if (source.type === 'custom_revenue') {
        const accountId = source.id.replace('custom_revenue_', '');
        const { data: daily } = await supabase
          .from('custom_revenue_daily')
          .select('date, revenue, impressions, clicks, page_views')
          .eq('account_id', accountId)
          .gte('date', compStart)
          .lte('date', compEnd);
        compData[source.id] = {
          daily: daily || [],
          totals: calculateCustomRevenueTotals(daily || []),
          isSpend: false
        };
      }
    }

    setCompareData(compData);
  };

  useEffect(() => { loadData(); }, [loadData]);
  
  // Load fetch usage on mount
  useEffect(() => { loadFetchUsage(); }, [loadFetchUsage]);

  // Load exchange rates on mount
  useEffect(() => { loadExchangeRates(); }, [loadExchangeRates]);

  // Load monthly data when sources change
  useEffect(() => {
    if (selectedSources.length > 0 && connectedSources.length > 0) {
      loadMonthlyData();
    }
  }, [selectedSources, connectedSources, loadMonthlyData]);

  // Save selection when changed
  useEffect(() => {
    if (user && selectedSources.length > 0) {
      localStorage.setItem(`dashboard_sources_${user.id}`, JSON.stringify(selectedSources));
    }
  }, [selectedSources, user]);

  // Build chart data when selection changes
  useEffect(() => {
    if (Object.keys(revenueData).length === 0) return;

    const dateMap = {};
    
    selectedSources.forEach(sourceId => {
      const source = revenueData[sourceId];
      if (!source?.daily) return;

      source.daily.forEach(row => {
        if (!dateMap[row.date]) {
          dateMap[row.date] = { date: row.date, revenue: 0, spend: 0 };
        }
        
        if (source.isSpend) {
          dateMap[row.date].spend += Number(row.spend) || 0;
        } else {
          dateMap[row.date].revenue += Number(row.net_revenue) || 0;
        }
      });
    });

    const sorted = Object.values(dateMap).sort((a, b) => a.date.localeCompare(b.date));
    setChartData(sorted);
  }, [selectedSources, revenueData]);

  // Toggle source selection
  const toggleSource = (sourceId) => {
    setSelectedSources(prev => {
      if (prev.includes(sourceId)) {
        return prev.filter(id => id !== sourceId);
      } else {
        return [...prev, sourceId];
      }
    });
  };

  // Calculate combined stats
  const calculateCombinedStats = (data) => {
    const combined = { totalRevenue: 0, totalSpend: 0, profit: 0, roas: 0, impressions: 0, clicks: 0 };

    selectedSources.forEach(sourceId => {
      const source = data[sourceId];
      if (!source?.totals) return;
      
      if (source.isSpend) {
        combined.totalSpend += source.totals.spend || 0;
      } else {
        combined.totalRevenue += source.totals.revenue || 0;
      }
      combined.impressions += source.totals.impressions || 0;
      combined.clicks += source.totals.clicks || 0;
    });

    combined.profit = combined.totalRevenue - combined.totalSpend;
    combined.roas = combined.totalSpend > 0 ? combined.totalRevenue / combined.totalSpend : 0;
    return combined;
  };

  const combined = calculateCombinedStats(revenueData);
  const compareCombined = compareEnabled ? calculateCombinedStats(compareData) : null;

  // Calculate percentage change
  const calcChange = (current, previous) => {
    if (!previous || previous === 0) return null;
    return ((current - previous) / previous) * 100;
  };

  // Format date range display
  const formatDateRange = () => {
    if (startDate === endDate) {
      return new Date(startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
    const start = new Date(startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const end = new Date(endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${start} - ${end}`;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Skeleton: top bar */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="h-9 w-40 bg-muted rounded-xl animate-pulse" />
          <div className="flex gap-2">
            <div className="h-9 w-28 bg-muted rounded-xl animate-pulse" />
            <div className="h-9 w-36 bg-muted rounded-xl animate-pulse" />
          </div>
        </div>
        {/* Skeleton: 4 stat cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => (
            <div key={i} className="rounded-2xl border border-border bg-card p-5 space-y-3">
              <div className="h-3 w-24 bg-muted rounded animate-pulse" />
              <div className="h-8 w-20 bg-muted rounded animate-pulse" />
              <div className="h-3 w-16 bg-muted rounded animate-pulse" />
            </div>
          ))}
        </div>
        {/* Skeleton: chart */}
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="h-4 w-32 bg-muted rounded animate-pulse mb-4" />
          <div className="h-48 bg-muted/50 rounded-xl animate-pulse" />
        </div>
        {/* Skeleton: source cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1,2,3].map(i => (
            <div key={i} className="rounded-2xl border border-border bg-card p-5 space-y-3">
              <div className="h-4 w-28 bg-muted rounded animate-pulse" />
              <div className="grid grid-cols-2 gap-3">
                <div className="h-3 w-16 bg-muted rounded animate-pulse" />
                <div className="h-3 w-16 bg-muted rounded animate-pulse" />
                <div className="h-5 w-20 bg-muted rounded animate-pulse" />
                <div className="h-5 w-20 bg-muted rounded animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Map source type → detail page URL (makes source cards clickable)
  const getSourceUrl = (source) => {
    if (!source) return null;
    if (source.type === 'admanager')     return '/ad-manager';
    if (source.type === 'adsense')       return '/adsense';
    if (source.type === 'meta')          return '/meta-ads';
    if (source.type === 'googleads')     return '/google-ads';
    if (source.type === 'tiktok')        return '/tiktok-ads';
    if (source.type === 'custom_ad' || source.type === 'custom_revenue') return '/custom-accounts';
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Trial/Subscription Banner */}
      {isTrialActive && daysRemaining <= 9 && (
        <Alert className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
          <Zap className="h-4 w-4 text-primary" />
          <AlertDescription className="flex items-center justify-between">
            <span>
              {daysRemaining <= 3 ? (
                <strong className="text-primary">Your trial ends in {daysRemaining} day{daysRemaining !== 1 ? 's' : ''}!</strong>
              ) : (
                <span>{daysRemaining} days left in your free trial.</span>
              )}
              {' '}Subscribe now to keep your data and features.
            </span>
            <Link to="/subscribe">
              <Button size="sm" className="ml-4">
                Choose a plan <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </AlertDescription>
        </Alert>
      )}

      {isTrialExpired && (
        <Alert variant="destructive">
          <X className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <span>
              <strong>Your trial has expired.</strong> Subscribe to continue using RevHapi.
            </span>
            <Link to="/subscribe">
              <Button size="sm" variant="outline" className="ml-4 border-white text-white hover:bg-card/20">
                Subscribe now <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </AlertDescription>
        </Alert>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Overview</h1>
          <p className="text-muted-foreground">
            {selectedSources.length} of {connectedSources.length} sources selected
          </p>
          {/* Most recent fetch across ALL configured sources. */}
          <LastUpdated
            value={connectedSources.reduce((latest, s) => {
              const t = s?.lastFetch;
              if (!t) return latest;
              return !latest || new Date(t) > new Date(latest) ? t : latest;
            }, null)}
            className="mt-2"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Fetch All Button */}
          <div className="relative">
            <Button 
              onClick={handleFetchAll}
              disabled={fetchingAll || fetchUsage.remaining <= 0}
              className="gap-2"
              title={`${fetchUsage.remaining} fetches remaining today`}
            >
              {fetchingAll ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              {fetchingAll && fetchProgress ? (
                <span className="flex items-center gap-1">
                  <span>{fetchProgress.current}</span>
                  <span className="text-xs opacity-75">({fetchProgress.index}/{fetchProgress.total})</span>
                </span>
              ) : (
                'Fetch All'
              )}
              {!fetchingAll && (
                <Badge variant="secondary" className="ml-1 text-xs">
                  {/* Display matches the Overview card: used / limit. */}
                  {fetchUsage.used}/{fetchUsage.limit}
                </Badge>
              )}
            </Button>
            {fetchUsage.remaining <= 0 && !fetchingAll && (
              <div className="absolute -bottom-6 left-0 right-0 text-center">
                <span className="text-xs text-red-500">Limit reached</span>
              </div>
            )}
            {fetchingAll && fetchProgress && (
              <div className="absolute -bottom-2 left-0 right-0">
                <div className="h-1 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-primary transition-all duration-500"
                    style={{ width: `${(fetchProgress.index / fetchProgress.total) * 100}%` }}
                  />
                </div>
              </div>
            )}
          </div>
          
          {/* Date Picker — shared component */}
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            defaultPreset={datePreset}
            showComparison={true}
            onChange={({ start, end, preset, compareEnabled: ce, comparePreset: cp, compareStart, compareEnd }) => {
              setStartDate(start);
              setEndDate(end);
              setDatePreset(preset || 'custom');
              setCompareEnabled(ce);
              setComparePreset(cp);
              // compareStart/compareEnd used by parent data fetch
            }}
          />
          
          <Button 
            variant="outline" 
            onClick={() => setShowSourceSelector(!showSourceSelector)}
          >
            <Settings className="h-4 w-4 mr-2" />
            Sources
          </Button>
        </div>
      </div>



      {/* Source Selector */}
      {showSourceSelector && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Select Data Sources</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowSourceSelector(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {connectedSources.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">
                No connected sources. Connect your ad accounts to see data here.
              </p>
            ) : (
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {connectedSources.map(source => (
                  <div
                    key={source.id}
                    onClick={() => toggleSource(source.id)}
                    className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                      selectedSources.includes(source.id) 
                        ? 'border-primary bg-primary/5' 
                        : 'border-border hover:border-border'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      selectedSources.includes(source.id)
                        ? 'bg-primary border-primary'
                        : 'border-border'
                    }`}>
                      {selectedSources.includes(source.id) && (
                        <Check className="h-3 w-3 text-white" />
                      )}
                    </div>
                    <span className="text-xl">{source.icon}</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{source.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{source.details}</p>
                    </div>
                    <Badge variant={revenueData[source.id]?.isSpend ? 'warning' : 'success'}>
                      {revenueData[source.id]?.isSpend ? 'Spend' : 'Revenue'}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Revenue"
          value={combined.totalRevenue}
          previousValue={compareCombined?.totalRevenue}
          compareEnabled={compareEnabled}
          color="green"
          icon={DollarSign}
          format="currency"
          currencyFormatter={formatDisplayCurrency}
        />
        <StatCard
          title="Total Spend"
          value={combined.totalSpend}
          previousValue={compareCombined?.totalSpend}
          compareEnabled={compareEnabled}
          color="red"
          icon={TrendingUp}
          format="currency"
          invertChange
          currencyFormatter={formatDisplayCurrency}
        />
        <StatCard
          title="Profit"
          value={combined.profit}
          previousValue={compareCombined?.profit}
          compareEnabled={compareEnabled}
          color={combined.profit >= 0 ? 'green' : 'red'}
          icon={BarChart3}
          format="currency"
          currencyFormatter={formatDisplayCurrency}
        />
        <StatCard
          title="ROAS"
          value={combined.roas}
          previousValue={compareCombined?.roas}
          compareEnabled={compareEnabled}
          color={combined.roas >= 1 ? 'blue' : 'red'}
          icon={TrendingUp}
          format="roas"
        />
      </div>

      {/* Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Revenue vs Spend</CardTitle>
        </CardHeader>
        <CardContent>
          {chartData.length === 0 ? (
            <div className="flex items-center justify-center h-[300px] text-muted-foreground">
              No data for selected sources
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis tickFormatter={(val) => {
                  const sym = CURRENCIES.find(c => c.code === displayCurrency)?.symbol || '$';
                  const converted = displayCurrency === 'USD' ? val : val * exchangeRates[displayCurrency];
                  return `${sym}${converted >= 1000 ? (converted/1000).toFixed(0) + 'k' : converted.toFixed(0)}`;
                }} />
                <Tooltip 
                  formatter={(value) => formatDisplayCurrency(value)}
                  labelFormatter={(date) => new Date(date).toLocaleDateString()}
                />
                <Legend />
                <Line type="monotone" dataKey="revenue" stroke="#22c55e" strokeWidth={2} name="Revenue" dot={false} />
                <Line type="monotone" dataKey="spend" stroke="#ef4444" strokeWidth={2} name="Spend" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>


      {/* HapiGuard banner */}
      <div className="rounded-xl border border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-950/70 px-4 py-3 flex items-center gap-3">
        <ShieldCheck className="h-5 w-5 text-emerald-500 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-200">HapiGuard click protection</p>
          <p className="text-xs text-emerald-800 dark:text-emerald-300">Protect your AdSense and Ad Manager revenue from invalid clicks and click bombing in real time.</p>
        </div>
        <Link to="/hapiguard" className="flex-shrink-0 text-xs font-semibold px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white transition-colors whitespace-nowrap">
          HapiGuard →
        </Link>
      </div>

      {/* Quick Stats by Source */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 items-stretch">
        {selectedSources.map(sourceId => {
          const source = connectedSources.find(s => s.id === sourceId);
          const data = revenueData[sourceId];
          const compData = compareEnabled ? compareData[sourceId] : null;
          if (!source || !data) return null;

          const mainValue = data.isSpend ? data.totals.spend : data.totals.revenue;
          const compValue = compData?.totals ? (data.isSpend ? compData.totals.spend : compData.totals.revenue) : null;
          const change = compValue ? calcChange(mainValue, compValue) : null;

          const sourceUrl = getSourceUrl(source);
          const CardWrapper = ({ children }) => sourceUrl
            ? <Link to={sourceUrl} className="block group">{children}</Link>
            : <>{children}</>;

          return (
            <CardWrapper key={sourceId}>
            <Card key={sourceId} className={`h-full flex flex-col${sourceUrl ? ' hover:border-primary/50 hover:shadow-md transition-all cursor-pointer' : ''}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <span>{source.icon}</span>
                    {source.name}
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    {source.lastFetch && (
                      <span className="text-xs text-muted-foreground">
                        {formatRelativeTime(source.lastFetch)}
                      </span>
                    )}
                    {sourceUrl && <span className="text-xs text-primary opacity-0 group-hover:opacity-100 transition-opacity">View →</span>}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">{data.isSpend ? 'Spend' : 'Revenue'}</p>
                    <p className={`text-lg font-bold ${data.isSpend ? 'text-red-600' : 'text-green-600'}`}>
                      {formatDisplayCurrency(mainValue)}
                    </p>
                    {change !== null && (
                      <p className={`text-xs ${change >= 0 ? (data.isSpend ? 'text-red-500' : 'text-green-500') : (data.isSpend ? 'text-green-500' : 'text-red-500')}`}>
                        {change >= 0 ? '↑' : '↓'} {Math.abs(change).toFixed(1)}%
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Impressions</p>
                    <p className="text-lg font-bold">{formatNumber(data.totals.impressions)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Clicks</p>
                    <p className="text-lg font-bold">{formatNumber(data.totals.clicks)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">CTR</p>
                    <p className="text-lg font-bold">
                      {data.totals.impressions > 0 
                        ? ((data.totals.clicks / data.totals.impressions) * 100).toFixed(2) 
                        : '0.00'}%
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            </CardWrapper>
          );
        })}
      </div>

      {/* Monthly Data Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Table className="h-5 w-5" />
              Monthly Performance
            </CardTitle>
            <div className="flex gap-1">
              {availableMonths.map(month => (
                <Button
                  key={month.key}
                  variant={selectedMonth === month.key ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedMonth(month.key)}
                >
                  {month.label.split(' ')[0]}
                </Button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {monthlyData[selectedMonth] ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-2 font-medium sticky left-0 bg-card">Metric</th>
                    {Object.keys(monthlyData[selectedMonth].days || {}).map(date => (
                      <th key={date} className="text-center py-2 px-1 font-medium min-w-[60px]">
                        {new Date(date).getDate()}
                      </th>
                    ))}
                    <th className="text-center py-2 px-2 font-bold bg-muted/40 sticky right-0">Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="py-2 px-2 font-medium sticky left-0 bg-card">Revenue</td>
                    {Object.values(monthlyData[selectedMonth].days || {}).map(day => {
                      const val = displayCurrency === 'USD' ? day.revenue : day.revenue * exchangeRates[displayCurrency];
                      const sym = CURRENCIES.find(c => c.code === displayCurrency)?.symbol || '$';
                      return (
                        <td key={day.date} className="text-center py-2 px-1 text-green-600">
                          {day.revenue > 0 ? `${sym}${val.toFixed(0)}` : '-'}
                        </td>
                      );
                    })}
                    <td className="text-center py-2 px-2 font-bold text-green-600 bg-muted/40 sticky right-0">
                      {formatDisplayCurrency(monthlyData[selectedMonth].totals?.revenue || 0)}
                    </td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 px-2 font-medium sticky left-0 bg-card">Spend</td>
                    {Object.values(monthlyData[selectedMonth].days || {}).map(day => {
                      const val = displayCurrency === 'USD' ? day.spend : day.spend * exchangeRates[displayCurrency];
                      const sym = CURRENCIES.find(c => c.code === displayCurrency)?.symbol || '$';
                      return (
                        <td key={day.date} className="text-center py-2 px-1 text-red-600">
                          {day.spend > 0 ? `${sym}${val.toFixed(0)}` : '-'}
                        </td>
                      );
                    })}
                    <td className="text-center py-2 px-2 font-bold text-red-600 bg-muted/40 sticky right-0">
                      {formatDisplayCurrency(monthlyData[selectedMonth].totals?.spend || 0)}
                    </td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 px-2 font-medium sticky left-0 bg-card">Profit</td>
                    {Object.values(monthlyData[selectedMonth].days || {}).map(day => {
                      const val = displayCurrency === 'USD' ? day.profit : day.profit * exchangeRates[displayCurrency];
                      const sym = CURRENCIES.find(c => c.code === displayCurrency)?.symbol || '$';
                      return (
                        <td key={day.date} className={`text-center py-2 px-1 ${day.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {day.revenue > 0 || day.spend > 0 ? `${sym}${val.toFixed(0)}` : '-'}
                        </td>
                      );
                    })}
                    <td className={`text-center py-2 px-2 font-bold bg-muted/40 sticky right-0 ${(monthlyData[selectedMonth].totals?.profit || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatDisplayCurrency(monthlyData[selectedMonth].totals?.profit || 0)}
                    </td>
                  </tr>
                  <tr className="border-b bg-blue-500/10">
                    <td className="py-2 px-2 font-medium sticky left-0 bg-blue-500/10 text-foreground">ROAS</td>
                    {Object.values(monthlyData[selectedMonth].days || {}).map(day => (
                      <td key={day.date} className={`text-center py-2 px-1 ${day.roas >= 1 ? 'text-blue-600' : 'text-orange-600'}`}>
                        {day.spend > 0 ? `${day.roas.toFixed(1)}x` : '-'}
                      </td>
                    ))}
                    <td className={`text-center py-2 px-2 font-bold bg-blue-500/10 sticky right-0 ${(monthlyData[selectedMonth].totals?.roas || 0) >= 1 ? 'text-blue-600' : 'text-orange-600'}`}>
                      {(monthlyData[selectedMonth].totals?.roas || 0).toFixed(2)}x
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              {loading ? 'Loading monthly data...' : 'No data available for this month'}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Currency Converter */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="flex items-center gap-2">
              <ArrowRightLeft className="h-5 w-5" />
              Currency Converter
            </CardTitle>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 bg-primary/10 rounded-lg px-3 py-1.5 border border-primary/20">
                <span className="text-sm font-medium">Display as:</span>
                <select
                  value={displayCurrency}
                  onChange={(e) => setDisplayCurrency(e.target.value)}
                  className="bg-transparent text-sm font-bold border-none focus:ring-0 cursor-pointer"
                >
                  {CURRENCIES.map(c => (
                    <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
                  ))}
                </select>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  setEditingRates({ ...exchangeRates });
                  setShowRateEditor(!showRateEditor);
                }}
              >
                <Settings className="h-4 w-4 mr-2" />
                Edit Rates
              </Button>
            </div>
          </div>
          {displayCurrency !== 'USD' && (
            <p className="text-sm text-muted-foreground mt-2">
              All monetary values on this page are displayed in {CURRENCIES.find(c => c.code === displayCurrency)?.name || displayCurrency} 
              {' '}(1 USD = {exchangeRates[displayCurrency]?.toLocaleString()} {displayCurrency})
            </p>
          )}
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            {/* Converter */}
            <div className="space-y-4">
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <label className="text-sm text-muted-foreground mb-1 block">Amount</label>
                  <input
                    type="number"
                    value={convertAmount}
                    onChange={(e) => setConvertAmount(e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-md bg-card text-foreground"
                    placeholder="Enter amount"
                  />
                </div>
                <div className="w-32">
                  <label className="text-sm text-muted-foreground mb-1 block">From</label>
                  <select
                    value={convertFrom}
                    onChange={(e) => setConvertFrom(e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-md bg-card text-foreground"
                  >
                    {CURRENCIES.map(c => (
                      <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
                    ))}
                  </select>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => {
                    const temp = convertFrom;
                    setConvertFrom(convertTo);
                    setConvertTo(temp);
                  }}
                >
                  <ArrowRightLeft className="h-4 w-4" />
                </Button>
                <div className="w-32">
                  <label className="text-sm text-muted-foreground mb-1 block">To</label>
                  <select
                    value={convertTo}
                    onChange={(e) => setConvertTo(e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-md bg-card text-foreground"
                  >
                    {CURRENCIES.map(c => (
                      <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="p-4 bg-muted/40 rounded-lg">
                <p className="text-sm text-muted-foreground">Result</p>
                <p className="text-2xl font-bold">
                  {CURRENCIES.find(c => c.code === convertTo)?.symbol}
                  {convertCurrency(parseFloat(convertAmount) || 0, convertFrom, convertTo).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  1 {convertFrom} = {(convertCurrency(1, convertFrom, convertTo)).toFixed(4)} {convertTo}
                </p>
              </div>
            </div>

            {/* Rate Editor */}
            {showRateEditor && (
              <div className="border rounded-lg p-4 max-h-[300px] overflow-y-auto">
                <div className="flex items-center justify-between mb-3">
                  <p className="font-medium">Exchange Rates (1 USD = ?)</p>
                  <Button size="sm" onClick={() => saveExchangeRates(editingRates)}>
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </Button>
                </div>
                <div className="grid gap-2">
                  {CURRENCIES.filter(c => c.code !== 'USD').map(currency => (
                    <div key={currency.code} className="flex items-center gap-2">
                      <span className="w-8">{currency.flag}</span>
                      <span className="w-12 font-medium">{currency.code}</span>
                      <input
                        type="number"
                        step="0.0001"
                        value={editingRates[currency.code] || ''}
                        onChange={(e) => setEditingRates(prev => ({
                          ...prev,
                          [currency.code]: parseFloat(e.target.value) || 0
                        }))}
                        className="flex-1 px-2 py-1 border border-border rounded text-sm bg-card text-foreground"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Premium Stat Card ────────────────────────────────────────────────────────
function StatCard({ title, value, previousValue, compareEnabled, color, icon: Icon, format, invertChange, currencyFormatter }) {
  const change = compareEnabled && previousValue ? ((value - previousValue) / Math.abs(previousValue)) * 100 : null;
  const formatValue = (val) => {
    if (format === 'currency' && currencyFormatter) return currencyFormatter(val);
    if (format === 'currency') return formatCurrency(val);
    if (format === 'percent') return `${val.toFixed(1)}%`;
    if (format === 'roas') return `${val.toFixed(2)}x`;
    return formatNumber(val);
  };
  const gradients = { green: 'from-emerald-500 to-teal-500', red: 'from-red-500 to-rose-500', blue: 'from-blue-500 to-indigo-500' };
  const isPositiveChange = invertChange ? change < 0 : change > 0;
  return (
    <div className="bg-card rounded-2xl border border-border shadow-sm p-5 hover:shadow-md transition-all duration-200 relative overflow-hidden">
      <div className="flex items-start justify-between mb-3">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">{title}</p>
        <div className={`p-2 rounded-xl bg-gradient-to-br ${gradients[color] || gradients.green} shadow-sm flex-shrink-0`}>
          <Icon className="h-4 w-4 text-white" />
        </div>
      </div>
      <p className="text-2xl font-bold tracking-tight text-foreground mb-2">{formatValue(value)}</p>
      {change !== null && (
        <div className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${isPositiveChange ? 'text-emerald-600 bg-emerald-500/10' : 'text-red-500 bg-red-500/10'}`}>
          {isPositiveChange ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
          {Math.abs(change).toFixed(1)}% vs prev
        </div>
      )}

    </div>
  );
}

// Helper functions
function calculateTotals(daily, revenueField, impressionsField, clicksField) {
  return {
    revenue: daily.reduce((sum, d) => sum + (Number(d[revenueField]) || 0), 0),
    impressions: daily.reduce((sum, d) => sum + (Number(d[impressionsField]) || 0), 0),
    clicks: daily.reduce((sum, d) => sum + (Number(d[clicksField]) || 0), 0),
  };
}

function calculateMetaTotals(daily) {
  return {
    spend: daily.reduce((sum, d) => sum + (Number(d.spend) || 0), 0),
    impressions: daily.reduce((sum, d) => sum + (Number(d.impressions) || 0), 0),
    clicks: daily.reduce((sum, d) => sum + (Number(d.clicks) || 0), 0),
  };
}

function calculateGoogleAdsTotals(daily) {
  return {
    spend: daily.reduce((sum, d) => sum + (Number(d.spend) || 0), 0),
    impressions: daily.reduce((sum, d) => sum + (Number(d.impressions) || 0), 0),
    clicks: daily.reduce((sum, d) => sum + (Number(d.clicks) || 0), 0),
    conversions: daily.reduce((sum, d) => sum + (Number(d.conversions) || 0), 0),
  };
}

function calculateCustomAdTotals(daily) {
  return {
    spend: daily.reduce((sum, d) => sum + (Number(d.spend) || 0), 0),
    impressions: daily.reduce((sum, d) => sum + (Number(d.impressions) || 0), 0),
    clicks: daily.reduce((sum, d) => sum + (Number(d.clicks) || 0), 0),
    conversions: daily.reduce((sum, d) => sum + (Number(d.conversions) || 0), 0),
  };
}

function calculateCustomRevenueTotals(daily) {
  return {
    revenue: daily.reduce((sum, d) => sum + (Number(d.revenue) || 0), 0),
    impressions: daily.reduce((sum, d) => sum + (Number(d.impressions) || 0), 0),
    clicks: daily.reduce((sum, d) => sum + (Number(d.clicks) || 0), 0),
    page_views: daily.reduce((sum, d) => sum + (Number(d.page_views) || 0), 0),
  };
}
